import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import QueryString from 'qs';
import moment from 'moment';
import { endOfWeekBy, startOfWeekBy, weekDatesBy } from '../../weekStart';
import { getPointTrainers, getBranchManager } from '../../../../api/staff';
import { getAllPointRooms } from '../../../../api/point';
import { getTicketsByType } from '../../../../api/ticket';
import { getAllLessonsByType, createLesson, updateLesson, deleteWeekById, getLessonById } from '../../../../api/lesson';
import { getMemberByBranchAndTemporary, getMemberByBranch } from '../../../../api/member-management';
import { getTemporaryUsersList } from '../../../../api/temporaryUser';
import { getUserTickets } from '../../../../api/user';

/**
 * 레슨 이벤트 데이터를 캘린더용으로 변환
 */
const transformLessonToEvent = (data, trainersList) => {
  const startDateTime = `${data?.classDate ?? ''}T${data?.startTime ?? '00:00'}:00.000Z`;
  const endDateTime = `${data?.classDate ?? ''}T${data?.endTime ?? '00:00'}:00.000Z`;

  return {
    absentedMemberCount: data?.absentedMemberCount,
    courseClassType: data?.courseClassType,
    attendedMemberCount: data?.attendedMemberCount,
    branchExerciseRoomName: data?.branchExerciseRoomName,
    registrationStartDate: data?.classDate,
    registrationEndDate: data?.classDate,
    start: startDateTime,
    end: endDateTime,
    className: data?.className,
    courseClassId: data?.courseClassId,
    courseClassTimeId: data?.courseClassTimeId,
    endTime: data?.endTime,
    exerciseRoomId: data?.exerciseRoomId?.toString() ?? '',
    membersMaxCount: data?.maxMemberCount,
    memberId: data?.memberId,
    memberName: data?.memberName,
    memberPhoneNumber: data?.memberPhoneNumber,
    memo: data?.memo,
    reservedMemberCount: data?.reservedMemberCount,
    startTime: data?.startTime,
    tickets: data?.tickets,
    trainerId: data?.trainerId,
    trainerName: data?.trainerName,
    column: trainersList.findIndex(trainer => trainer?.trainerId === data?.trainerId),
    startHour: parseInt(data?.startTime?.split(':')[0] ?? '0'),
    startMinute: parseInt(data?.startTime?.split(':')[1] ?? '0'),
    endHour: parseInt(data?.endTime?.split(':')[0] ?? '0'),
    endMinute: parseInt(data?.endTime?.split(':')[1] ?? '0'),
    members: data?.members || [],
    // 상담 관련 필드
    consultationId: data?.consultationId,
    openYn: data?.openYn,
    managerId: data?.managerId,
    managerName: data?.managerName,
    consultMethod: data?.consultMethod,
    consultPurpose: data?.consultPurpose,
    consultType: data?.consultType,
    consultResult: data?.consultResult,
    content: data?.content,
    // 목록 노출용 절단본이라 상담 수정 폼에 프리필하면 안 된다 (원문은 상담 상세 API)
    consultContentPreview: data?.consultContentPreview,
    // 개인레슨 수강권 세션 정보 (PRIVATE_LESSON일 때만 내려옴)
    totalSessionCount: data?.totalSessionCount,
    remainingSessionCount: data?.remainingSessionCount,
    // 기타 일정 식별자 (ETC일 때만 내려옴). 내용은 memo 에 원문 그대로 실려 온다.
    branchScheduleId: data?.branchScheduleId,
  };
};

/**
 * 개인 레슨 데이터 조회 및 관리 훅
 * @param view - 'month' | 'week' | 'day'
 */
export const usePrivateLessonData = ({ branchId, headBranchId, token, currentDate, view, customRange, weekStartDay }) => {
  const queryClient = useQueryClient();

  // 날짜 범위 계산 (기간 조회=선택 범위 그대로, 월=당월만(이월 셀 이벤트는 안 가져온다), 주=설정한 주 시작 요일부터 7일, 일=단일일)
  const dateRange = useMemo(() => {
    if (customRange) {
      return { start: customRange.start, end: customRange.end };
    }
    if (view === 'month') {
      return {
        start: currentDate.clone().startOf('month').format('YYYY-MM-DD'),
        end: currentDate.clone().endOf('month').format('YYYY-MM-DD'),
      };
    }
    if (view === 'week') {
      return {
        start: startOfWeekBy(currentDate, weekStartDay).format('YYYY-MM-DD'),
        end: endOfWeekBy(currentDate, weekStartDay).format('YYYY-MM-DD'),
      };
    }
    return {
      start: currentDate.format('YYYY-MM-DD'),
      end: currentDate.format('YYYY-MM-DD'),
    };
  }, [currentDate, view, customRange, weekStartDay]);

  // 강사 목록 조회
  const {
    data: trainers = [],
    isLoading: isLoadingTrainers,
    refetch: refetchTrainers,
  } = useQuery({
    queryKey: ['trainers', branchId],
    queryFn: () => getPointTrainers(branchId, token),
    enabled: !!branchId && !!token,
    staleTime: 5 * 60 * 1000, // 5분간 캐시
  });

  // 지점 임직원(매니저) 목록 조회 — 상담 담당 매니저가 강사 목록에 없어도 필터/표시에 잡히도록
  const { data: branchManagers = [], isLoading: isLoadingManagers } = useQuery({
    queryKey: ['branchManagers', branchId],
    queryFn: () => getBranchManager(branchId, token),
    enabled: !!branchId && !!token,
    staleTime: 5 * 60 * 1000,
  });

  // 통합 임직원 목록 — 강사 + 강사가 아닌 임직원을 합쳐 sortOrder(branch_trainer_order) 기준으로 정렬.
  // 순서가 저장된 사람이 앞(오름차순), 미지정은 뒤(강사 → 비강사 순서 유지).
  // 상담 이벤트는 trainerId 자리에 managerId(=employeeId)가 오므로 이 목록이
  // 강사 필터·일별 컬럼·월별 그룹·주간 드롭다운의 공통 소스가 된다.
  const staffList = useMemo(() => {
    const trainerIds = new Set(trainers.map(trainer => trainer.trainerId));
    const extraManagers = branchManagers
      .filter(manager => !trainerIds.has(manager.employeeId))
      .map(manager => ({
        trainerId: manager.employeeId,
        trainerName: manager.managerName,
        employeePositionName: manager.employeePositionName ?? null, // 직급명 (필터 그룹핑용, 미지정 null)
        sortOrder: manager.sortOrder ?? null,
        isTrainer: false,
      }));
    const merged = [...trainers.map(trainer => ({ ...trainer, isTrainer: true })), ...extraManagers];
    return [
      ...merged.filter(staff => staff.sortOrder != null).sort((a, b) => a.sortOrder - b.sortOrder),
      ...merged.filter(staff => staff.sortOrder == null),
    ];
  }, [trainers, branchManagers]);

  // 강의실 목록 조회
  // 센터설정에서 유형·기본 강의실을 바꾸면 바로 반영돼야 해서 캐시를 오래 잡지 않는다.
  const { data: rooms = [], isLoading: isLoadingRooms } = useQuery({
    queryKey: ['rooms', branchId],
    queryFn: () => getAllPointRooms(branchId, token),
    enabled: !!branchId && !!token,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // 레슨 목록 조회 — 기간 이동 시 이전 데이터를 유지한 채 갱신 (화면 언마운트 방지, v5 placeholderData)
  const {
    data: events = [],
    isLoading: isLoadingEvents,
    isFetching: isFetchingEvents,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: ['privateLessons', branchId, dateRange.start, dateRange.end],
    queryFn: async () => {
      const query = QueryString.stringify({
        courseClassType: 'ALL',
        headBranchId,
        branchId,
        classDateFrom: dateRange.start,
        classDateTo: dateRange.end,
      });
      const data = await getAllLessonsByType(query, token);
      return data?.map(item => transformLessonToEvent(item, staffList)) || [];
    },
    enabled: !!branchId && !!token && staffList.length > 0 && !isLoadingManagers,
    staleTime: 30 * 1000, // 30초간 캐시
    placeholderData: keepPreviousData,
  });

  // 레슨 생성 mutation
  const createLessonMutation = useMutation({
    mutationFn: data => createLesson(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privateLessons', branchId] });
    },
  });

  // 레슨 수정 mutation
  const updateLessonMutation = useMutation({
    mutationFn: data => updateLesson(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privateLessons', branchId] });
    },
  });

  // 레슨 삭제 mutation
  const deleteLessonMutation = useMutation({
    mutationFn: courseClassTimeId => deleteWeekById(courseClassTimeId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privateLessons', branchId] });
    },
  });

  // 레슨 상세 조회
  const getLessonDetail = useCallback(
    async courseClassId => {
      return getLessonById(courseClassId, token);
    },
    [token],
  );

  // 데이터 새로고침
  const refreshAll = useCallback(() => {
    refetchTrainers();
    refetchEvents();
  }, [refetchTrainers, refetchEvents]);

  const isLoading = isLoadingTrainers || isLoadingManagers || isLoadingRooms || isLoadingEvents;

  return {
    // 데이터
    trainers,
    staffList,
    rooms,
    events,

    // 로딩 상태
    isLoading,
    isLoadingTrainers,
    isLoadingRooms,
    isLoadingEvents,
    isFetchingEvents,

    // 뮤테이션
    createLesson: createLessonMutation.mutate,
    updateLesson: updateLessonMutation.mutate,
    deleteLesson: deleteLessonMutation.mutate,
    isCreating: createLessonMutation.isPending,
    isUpdating: updateLessonMutation.isPending,
    isDeleting: deleteLessonMutation.isPending,

    // 유틸리티
    getLessonDetail,
    refreshAll,
    refetchEvents,
  };
};

// 회원권 문자열 파싱 (이름|CLASS_TYPE|STATUS|잔여횟수|시작일|만료일)
const parseTicketString = str => {
  if (!str) return [];
  return str.split(';;').map(item => {
    const [name, classType, status, remaining, startDate, endDate] = item.split('|');
    return { name, classType, status, remaining, startDate, endDate };
  });
};

/**
 * 회원 검색 훅
 * @param memberType - 'MEMBER' (유효회원만), 'TEMPORARY' (잠재회원만), 'ALL' (전체)
 */
export const useMemberSearch = ({ headBranchId, branchId, token }) => {
  const searchMembers = useCallback(
    async (searchValue, memberType = 'ALL', lessonType, assignedTrainerId) => {
      const isPrivateOrDuet =
        lessonType === 'DUET_LESSON' || lessonType === 'PRIVATE_LESSON' || lessonType === 'OT';
      // 개인·듀엣·OT는 빈 검색어도 허용 — 검색창 포커스만으로 후보 전체를 보여준다
      if ((!searchValue || searchValue.trim().length === 0) && !isPrivateOrDuet) {
        return [];
      }

      const queryParams = {
        searchInput: searchValue,
        headBranchId,
        branchId,
      };
      // 레슨 타입에 따라 classTypes 필터 + 활성 회원만
      if (lessonType === 'DUET_LESSON') {
        queryParams.classTypes = ['DUET_LESSON'];
        queryParams.ticketStatus = 'ACTIVE';
      } else if (lessonType === 'PRIVATE_LESSON') {
        queryParams.classTypes = ['PRIVATE_LESSON'];
        queryParams.ticketStatus = 'ACTIVE';
      } else if (lessonType === 'OT') {
        queryParams.classTypes = ['OT'];
        queryParams.ticketStatus = 'ACTIVE';
      }
      // 담당 회원만 보기 — 수강권 배정 강사(employee) 기준
      if (isPrivateOrDuet && assignedTrainerId) {
        queryParams.assignedTrainerId = assignedTrainerId;
      }
      const qstring = QueryString.stringify(queryParams, { arrayFormat: 'repeat' });

      const mapItem = item => ({
        id: item.memberId || item.userId,
        name: item.name,
        phoneNumber: item.phoneNumber,
        userType: item.userType || 'MEMBER',
        tickets: [
          ...parseTicketString(item.courseTickets),
          ...parseTicketString(item.coursePasses),
          ...parseTicketString(item.trialTickets),
        ],
      });

      let data;
      if (memberType === 'MEMBER') {
        data = await getMemberByBranch(token, qstring);
        return data?.content?.map(item => ({ ...mapItem(item), userType: item.userType || 'MEMBER' })) || [];
      } else if (memberType === 'TEMPORARY') {
        const tempQuery = QueryString.stringify({
          searchInput: searchValue,
          headBranchId,
          branchId,
          page: 1,
          limit: 50,
        });
        data = await getTemporaryUsersList(token, tempQuery);
        return data?.content?.map(item => ({ ...mapItem(item), userType: 'TEMPORARY' })) || [];
      } else {
        data = await getMemberByBranchAndTemporary(token, qstring);
        return data?.content?.map(mapItem) || [];
      }
    },
    [headBranchId, branchId, token],
  );

  return { searchMembers };
};

/**
 * 수강권 조회 훅
 */
export const useTickets = ({ trainerId, branchId, courseClassType, token, enabled = true }) => {
  return useQuery({
    queryKey: ['tickets', branchId, trainerId, courseClassType],
    queryFn: async () => {
      const queryString = QueryString.stringify({
        trainerId,
        branchId,
        courseClassType,
      });
      return getTicketsByType(queryString, token);
    },
    enabled: !!trainerId && !!branchId && !!token && enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * 회원 수강권 조회 훅
 */
export const useMemberTickets = ({ memberId, token, enabled = true }) => {
  return useQuery({
    queryKey: ['memberTickets', memberId],
    queryFn: () => getUserTickets(memberId, token),
    enabled: !!memberId && !!token && enabled,
  });
};

/**
 * 유틸리티: 요일 문자열 반환
 */
export const getDayOfWeek = dateString => {
  const date = new Date(dateString);
  const daysOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
  return daysOfWeek[date.getDay()];
};

/**
 * 유틸리티: 시간을 인덱스로 변환
 */
export const timeToIndex = dateString => {
  const date = new Date(dateString);
  const hour = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  return hour + minutes / 60;
};

/**
 * 유틸리티: 값 범위 제한
 */
export const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

/**
 * 유틸리티: 수업 시간 길이 계산 (분 단위)
 */
export const calculateDuration = (startTime, endTime) => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  return endMinutes - startMinutes;
};

/**
 * 유틸리티: 이벤트 유형 키 (월/주 보기 색·배지 공통)
 */
export const getEventTypeKey = courseClassType =>
  courseClassType === 'CONSULTATION'
    ? 'consult'
    : courseClassType === 'DUET_LESSON'
      ? 'duet'
      : courseClassType === 'OT'
        ? 'ot'
      : courseClassType === 'GROUP_LESSON'
        ? 'group'
        : courseClassType === 'ETC'
          ? 'etc'
          : 'pt';

/**
 * 유틸리티: 이벤트 카드 React key (일간/주간/월간 공통)
 *
 * 상담은 courseClassTimeId 가 없고 consultationId 를 쓴다. 수업 ID 와 상담 ID 는 서로 다른
 * 채번이라 값이 겹칠 수 있으므로 상담에는 접두어를 붙여 구분한다.
 * ID 가 아예 없을 때만 호출부가 넘긴 fallback(인덱스 기반)을 쓴다.
 */
export const getEventCardKey = (event, fallback) => {
  if (event.courseClassTimeId) return event.courseClassTimeId;
  if (event.consultationId) return `consultation-${event.consultationId}`;
  if (event.branchScheduleId) return `branch-schedule-${event.branchScheduleId}`;
  return fallback;
};

/**
 * 유틸리티: 주간 날짜 배열 생성 (weekStartDay = 주 시작 요일, 지점 스케줄 설정)
 */
export const getWeekDates = (currentDate, weekStartDay) => weekDatesBy(currentDate, weekStartDay);
